import { useQueries, UseQueryResult } from 'react-query'
import axios from 'axios'
import Interval from 'date-fns/esm'
import format from 'date-fns/format'
import uniqBy from 'lodash/uniqBy'

import {
  getMonthViewVisibleInterval,
  getWeekViewInterval,
} from '@/components/Calendar/Calendar'
import { CalendarParams } from '@/queries/useCalendarParams'

export type UseCalendarEventsProps = {
  url: string
  calendarParams: CalendarParams
}

export const getCalendarRequestIntervals = ({
  date,
  view,
}: CalendarParams): Interval[] => {
  if (view !== 'month') {
    // Preserve the existing behavior for day, week and list views: they all
    // load the containing week.
    return [getWeekViewInterval(date)]
  }

  return [getMonthViewVisibleInterval(date)]
}

export const getCalendarEventQueryOptions = <T>(
  url: string,
  intervals: Interval[],
) =>
  intervals.map(interval => {
    const params = {
      since: format(interval.start, 'yyyy-MM-dd'),
      until: format(interval.end, 'yyyy-MM-dd'),
    }

    return {
      queryKey: [url, params],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        axios
          .get<T[]>(url, {
            params,
            signal,
          })
          .then(response => response.data),
    }
  })

export const useCalendarEvents = <T>({
  url,
  calendarParams,
}: UseCalendarEventsProps) => {
  const intervals = getCalendarRequestIntervals(calendarParams)
  const results = useQueries(
    getCalendarEventQueryOptions<T>(url, intervals),
  ) as UseQueryResult<T[]>[]

  const events =
    // Preserve the hook's existing guarantee that every event id is returned
    // only once, even though the calendar now loads a single range.
    uniqBy(
      results
        // get data from all queries
        .map(({ data }) => data)
        // filter out unfinished queries data
        .filter((data): data is T[] => data !== undefined)
        // merge all queries data into one array
        .flat(),
      'id',
    )
  const isFetching = results.some(({ isFetching }) => isFetching)
  const refetch = async () =>
    await Promise.all(results.map(({ refetch }) => refetch()))

  return {
    events,
    isFetching,
    refetch,
  }
}
